// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file LayoutHeader.tsx
 * @input Uses React StyleX
 * @output Exports LayoutHeader component and LayoutHeaderProps
 * @position Top bar / header area for Layout. Use for page titles, app bars,
 *   toolbar areas, or any fixed-height content at the top of a layout.
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/Layout/Layout.doc.mjs
 * - /apps/storybook/stories/Layout.stories.tsx
 * - /packages/cli/assets/templates/blocks/components/Layout/ (showcase blocks)
 */
import { use } from 'react';
import * as stylex from '@stylexjs/stylex';
import { LayoutDividerContext } from "./LayoutDividerContext.js";
import "../theme/tokens.stylex.js";
import { colorVars, spacingVars } from "../theme/tokens.stylex.js";
import { mergeProps } from "../utils/index.js";
import { themeProps } from "../utils/themeProps.js";
import { paddingStyles, containerPaddingInlineVarStyles, containerPaddingBlockStartVarStyles, containerPaddingBlockEndVarStyles } from "./padding.stylex.js";
import { jsx as _jsx } from "react/jsx-runtime";
const styles = {
  header: {
    kmuXW: "x2lah0s",
    $$css: true
  },
  inner: {
    kB7OPa: "x9f619",
    ks0D6T: "xjl2t3p",
    kUOVxO: "xvueqy4",
    kZCmMZ: "x139j0dd",
    kwRFfy: "xpc6k2p",
    kLKAdn: "x81pis9",
    kGO01o: "xg476vw",
    "--container-padding-inline-start": "xdvaxxn",
    "--container-padding-inline-end": "xqpvj4r",
    "--container-padding-block-start": "xzz8v79",
    "--container-padding-block-end": "xi9ns85",
    $$css: true
  },
  fullBleed: {
    kZCmMZ: "x1c1uobl",
    kwRFfy: "xyri2b",
    kLKAdn: "xexx8yu",
    kGO01o: "x18d9i69",
    "--container-padding-inline-start": "xrhngw9",
    "--container-padding-inline-end": "xjsfl84",
    "--container-padding-block-start": "x1047aw6",
    "--container-padding-block-end": "xax9j7h",
    $$css: true
  },
  divider: {
    kt9PQ7: "xso031l",
    kfdmCh: "x1q0q8m5",
    kL6WhQ: "xw8gpjh",
    $$css: true
  }
};

// Dynamic styles for sizing props
const dynamicStyles = {
  sizing: height => [{
    kZKoxP: height != null ? "x16ye13r" : height,
    $$css: true
  }, {
    "--x-height": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(height)
  }]
};
/**
 * Top bar / header for Layout. Use for page titles, app bars, or toolbars.
 * Renders in the header slot with optional divider and padding control.
 *
 * Already provides its own padding — don't add padding to children.
 * Use `padding={0}` if your content manages its own padding (e.g. TopNav).
 *
 * @example
 * ```
 * <LayoutContainer variant="card">
 *   <Layout
 *     header={<LayoutHeader hasDivider>Page Title</LayoutHeader>}
 *     content={<LayoutContent>...</LayoutContent>}
 *   />
 * </LayoutContainer>
 * ```
 */
export function LayoutHeader({
  children,
  hasDivider,
  height,
  label,
  padding,
  role,
  xstyle,
  className,
  style,
  ref,
  ...props
}) {
  const dividerCtx = use(LayoutDividerContext);
  const resolvedHasDivider = hasDivider ?? dividerCtx?.defaultHasDividers ?? false;
  const isZeroPadding = padding === 0;
  return /*#__PURE__*/_jsx("div", {
    ref: ref,
    role: role,
    "aria-label": label,
    "data-divider": resolvedHasDivider || undefined,
    ...mergeProps(themeProps('layout-header'), stylex.props(styles.header, dynamicStyles.sizing(height ?? null), resolvedHasDivider && styles.divider, xstyle), className, style),
    ...props,
    children: /*#__PURE__*/_jsx("div", {
      ...stylex.props(styles.inner, isZeroPadding && styles.fullBleed, padding != null && paddingStyles[padding], padding != null && containerPaddingInlineVarStyles[padding], padding != null && containerPaddingBlockStartVarStyles[padding], padding != null && containerPaddingBlockEndVarStyles[padding]),
      children: children
    })
  });
}
LayoutHeader.displayName = 'LayoutHeader';